// The "Print Label" button. One POST for the ZPL, then straight to the local
// printer via Browser Print — no page reload, no PDF, no print dialog.

import ZebraBrowserPrint, { BrowserPrintError } from './zebra_browser_print'

const csrf = () => {
  const tag = document.querySelector("meta[name='csrf-token']")
  return tag ? tag.getAttribute('content') : ''
}

async function requestJson(url, { method = 'GET', body = null } = {}) {
  const headers = { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
  if (body) Object.assign(headers, { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf() })

  const response = await fetch(url, {
    method,
    credentials: 'same-origin',
    headers,
    body: body ? JSON.stringify(body) : undefined
  })

  const payload = await response.json().catch(() => null)
  if (!response.ok) {
    const error = (payload && payload.error) || {}
    throw new LabelServerError(
      error.code || 'server_error',
      error.message || 'The server could not build these labels.',
      error.action_path
    )
  }
  return payload
}

const getJson = (url) => requestJson(url)
const postJson = (url, body = {}) => requestJson(url, { method: 'POST', body })

class LabelServerError extends Error {
  constructor(code, message, actionPath) {
    super(message)
    this.name = 'LabelServerError'
    this.code = code
    this.actionPath = actionPath
  }
}

// Standalone entry point for pages that already have their own Alpine data and
// just want "fetch the payload, put it on the printer" — the inventory item
// and purchase order screens. Uses SweetAlert for the printer picker since
// those pages have no picker markup of their own.
async function printLabelsFrom(url, { method = 'GET', body = null } = {}) {
  try {
    const payload = await requestJson(url, { method, body })
    const resolved = await ZebraBrowserPrint.resolvePrinter()

    let printer = resolved.printer
    if (!printer) {
      printer = await askForPrinter(resolved.printers)
      if (!printer) return false
    }
    if (resolved.source !== 'remembered') ZebraBrowserPrint.remember(printer.uid)

    await sendLabels(printer, payload, window.notify)
    return true
  } catch (e) {
    reportPrintError(e, window.notify)
    return false
  }
}

// printer + payload -> paper, plus the two notifications that follow. Shared
// so the standalone helper and the Alpine component can't drift apart.
async function sendLabels(printer, payload, notify) {
  await ZebraBrowserPrint.send(printer, payload.labels)

  notify('success', `${jobSummary(payload)} sent to ${printer.name}`)
  if (payload.warnings && payload.warnings.length) notify('warning', payload.warnings.join(' \u00b7 '))
}

// every failure gets a sentence saying what to actually do about it
function reportPrintError(e, notify) {
  if (e instanceof LabelServerError || e instanceof BrowserPrintError) {
    notify('error', e.message)
    return e instanceof LabelServerError ? e.actionPath : null
  }
  // eslint-disable-next-line no-console
  console.error('[label] unexpected print failure', e)
  notify('error', 'The labels could not be printed. Check that Zebra Browser Print is running, then try again.')
  return null
}

// "3 labels · Laptop Inventory" — with a picker and a default both in play,
// which template came out is worth saying
function jobSummary(payload) {
  const count = payload.labels.length
  const noun = count > 1 ? `${count} labels` : 'Label'
  const names = (payload.templates || []).map((t) => t.name)
  if (!names.length) return noun
  return `${noun} \u00b7 ${names.length > 1 ? `${names.length} templates` : names[0]}`
}

async function askForPrinter(printers) {
  const options = printers.reduce((acc, p) => ({ ...acc, [p.uid]: p.name }), {})
  const { value: uid } = await window.Swal.fire({
    title: 'Choose a printer',
    input: 'select',
    inputOptions: options,
    inputPlaceholder: 'Select a Zebra printer',
    showCancelButton: true
  })
  return printers.find((p) => p.uid === uid) || null
}

window.printLabelsFrom = printLabelsFrom

document.addEventListener('alpine:init', () => {
  window.Alpine.data('labelPrinter', (config = {}) => ({
    printUrl: config.printUrl,
    templatesUrl: config.templatesUrl,
    testPrintUrl: config.testPrintUrl,

    menuOpen: false,
    busy: false,
    printers: [],
    templates: [],
    pendingJob: null,
    pickerOpen: false,
    templatePickerOpen: false,

    get printerName() {
      const uid = ZebraBrowserPrint.remembered()
      const match = this.printers.find((p) => p.uid === uid)
      return match ? match.name : null
    },

    async printDefault() {
      this.menuOpen = false
      await this.run(async () => {
        const payload = await postJson(this.printUrl)
        await this.withPrinter((printer) => this.sendTo(printer, payload))
      })
    },

    // the label names the printer it came out of, so the payload can't be
    // fetched until we know which one
    async testPrint() {
      this.menuOpen = false
      await this.run(() =>
        this.withPrinter(async (printer) => {
          const payload = await postJson(this.testPrintUrl, { printer_name: printer.name })
          await ZebraBrowserPrint.send(printer, payload.labels)
          this.notify('success', `Test label sent to ${printer.name}`)
        })
      )
    },

    async chooseTemplate() {
      this.menuOpen = false
      await this.run(async () => {
        const data = await getJson(this.templatesUrl)
        this.templates = data.templates || []
        if (!this.templates.length) {
          this.notify('error', `No active label templates exist for ${data.device_type || 'this device type'}.`)
          return
        }
        this.templatePickerOpen = true
      })
    },

    async printWithTemplate(templateId) {
      this.templatePickerOpen = false
      await this.run(async () => {
        const payload = await postJson(this.printUrl, { template_id: templateId })
        await this.withPrinter((printer) => this.sendTo(printer, payload))
      })
    },

    // Runs job(printer) once a printer is known. If we have to ask, the job is
    // parked and selectPrinter() resumes it — otherwise picking a printer would
    // just dismiss the modal and drop whatever the user asked for.
    async withPrinter(job) {
      const { printer } = await this.pickPrinter()
      if (!printer) {
        this.pendingJob = job
        return
      }
      await job(printer)
    },

    sendTo(printer, payload) {
      return sendLabels(printer, payload, (icon, title) => this.notify(icon, title))
    },

    // returns {printer: null} when the user has to choose; the modal calls
    // selectPrinter() to continue
    async pickPrinter() {
      const resolved = await ZebraBrowserPrint.resolvePrinter()
      this.printers = resolved.printers

      if (resolved.printer) {
        if (resolved.source !== 'remembered') ZebraBrowserPrint.remember(resolved.printer.uid)
        return resolved
      }

      this.pickerOpen = true
      return { printer: null, printers: resolved.printers }
    },

    async selectPrinter(uid) {
      const printer = this.printers.find((p) => p.uid === uid)
      if (!printer) return
      ZebraBrowserPrint.remember(uid)
      this.pickerOpen = false

      const job = this.pendingJob
      this.pendingJob = null
      if (!job) return

      await this.run(() => job(printer))
    },

    cancelPicker() {
      this.pickerOpen = false
      this.pendingJob = null
    },

    forgetPrinter() {
      ZebraBrowserPrint.forget()
      this.notify('success', 'Printer choice cleared — you will be asked again next time.')
    },

    async run(work) {
      if (this.busy) return
      this.busy = true
      try {
        await work()
      } catch (e) {
        this.reportError(e)
      } finally {
        this.busy = false
      }
    },

    // the device page can offer somewhere to go; the standalone helper can't
    reportError(e) {
      const actionPath = reportPrintError(e, (icon, title) => this.notify(icon, title))
      if (actionPath) window.setTimeout(() => { window.location.href = actionPath }, 2500)
    },

    notify(icon, title) {
      if (typeof window.notify === 'function') window.notify(icon, title)
    }
  }))
})
